// Below 992px the form's three columns become tabs, one shown at a time
export const MOBILE_TAB_QUERY = '(max-width: 991.98px)';

// The Song Info fields, so a blocked save can point at the tab holding the reason
export const SONG_INFO_FIELDS = [
  'title',
  'artist',
  'key',
  'transposeToKey',
  'bpm',
  'timeSignature',
  'categoryId',
  'themes',
  'youtube',
];

/**
 * A new song opens on Info, since it can't be saved without it. A part opened from the song view
 * opens on Lyrics; any other edit opens on Parts, as a song's info is rarely touched after it's made.
 */
export const startingTab = (isNewSong, search = '') => {
  if (isNewSong) return 'info';

  return new URLSearchParams(search).has('editPartId') ? 'lyrics' : 'parts';
};

/**
 * Which tabs get a dot: Info while one of its fields fails validation, Lyrics while a part is
 * open in the editor. Either one keeps Save disabled.
 */
export const tabsNeedingAttention = ({ validationErrors, editPartId }) => ({
  info: SONG_INFO_FIELDS.some((field) => validationErrors?.[field]),
  lyrics: !!editPartId,
});

export const TAB_ORDER = ['info', 'lyrics', 'parts'];

// Far enough to be meant, and mostly sideways, so a scroll that drifts doesn't change tabs. Quick,
// too: selecting text starts with a long press, so a slow drag over the lyrics is left to the textarea.
const SWIPE_MIN_DISTANCE = 60;
const SWIPE_MAX_SLOPE = 0.5;
const SWIPE_MAX_DURATION = 600;

/** The tab a swipe of (dx, dy) over `ms` lands on from `tab`, or null when it isn't one or there's nowhere to go */
export const swipedTab = (tab, dx, dy, ms = 0) => {
  if (ms > SWIPE_MAX_DURATION) return null;
  if (Math.abs(dx) < SWIPE_MIN_DISTANCE || Math.abs(dy) > Math.abs(dx) * SWIPE_MAX_SLOPE) return null;

  // Swiping left brings in the next tab, the way a page turns
  return TAB_ORDER[TAB_ORDER.indexOf(tab) + (dx < 0 ? 1 : -1)] || null;
};

/**
 * A touch that starts on something with a sideways gesture of its own (a part's drag handle, a slider,
 * a strip, or a field being typed in whose text scrolls sideways) is left to it rather than read as a swipe between tabs.
 */
export const ownsSwipe = (target, root) => {
  for (let el = target; el && el !== root; el = el.parentElement) {
    const style = getComputedStyle(el);
    // A field only scrolls its text sideways while it's being typed in
    const scrollsSideways = el.matches('input')
      ? el === document.activeElement
      : ['auto', 'scroll'].includes(style.overflowX);

    if (el.matches('.drag-handle, input[type="range"]') || style.touchAction === 'none') return true;
    if (scrollsSideways && el.scrollWidth > el.clientWidth) return true;
  }

  return false;
};

/** A drag that ended with text selected in a field was selecting it, not swiping */
export const selectedText = (el) => el?.matches?.('input, textarea') && el.selectionStart !== el.selectionEnd;
